import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { logger } from '@/shared/utils';
import { useRegister } from '../features/user';
import { FormError, Input } from '@/shared/components/ui';
import { APP_CONFIG, ROUTES } from '@/shared/config';
import { useSafeThemeClasses } from '@/shared/theme';
import toast from 'react-hot-toast';

interface FormData {
	email: string;
	username: string;
	password: string;
	confirmPassword: string;
	firstName?: string;
	lastName?: string;
}

interface FormErrors {
	[key: string]: string;
}

function RegisterForm() {
	const [formData, setFormData] = useState<FormData>({
		email: '',
		username: '',
		password: '',
		confirmPassword: '',
		firstName: '',
		lastName: '',
	});
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();

	// Use the new isolated register operation
	const { register, loading: registerLoading, error: registerError } = useRegister();
	
	// theme theme classes
	const theme = useSafeThemeClasses();

	const validateForm = (): boolean => {
		const newErrors: FormErrors = {};

		// Email validation
		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'Please enter a valid email address';
		}

		// Username validation
		if (!formData.username.trim()) {
			newErrors.username = 'Username is required';
		} else if (formData.username.length < 3) {
			newErrors.username = 'Username must be at least 3 characters long';
		}

		// Password validation
		if (!formData.password.trim()) {
			newErrors.password = 'Password is required';
		} else if (formData.password.length < 6) {
			newErrors.password = 'Password must be at least 6 characters long';
		}

		// Confirm password validation
		if (!formData.confirmPassword.trim()) {
			newErrors.confirmPassword = 'Please confirm your password';
		} else if (formData.password !== formData.confirmPassword) {
			newErrors.confirmPassword = 'Passwords do not match';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
		
		// Clear error for this field if it exists
		if (errors[name]) {
			setErrors(prev => ({ ...prev, [name]: '' }));
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		logger.info('User registration attempt', 'USER', {
			email: formData.email,
			username: formData.username
		});

		try {
			// Use the new isolated register operation
			const success = await register({
				email: formData.email,
				username: formData.username,
				password: formData.password,
				first_name: formData.firstName,
				last_name: formData.lastName,
			});
			
			if (success) {
				// Registration was successful
				logger.info('User registration successful', 'USER', {
					email: formData.email,
					username: formData.username
				});

				// Show success toaster
				toast.success('🎉 Registration successful! Welcome to ' + APP_CONFIG.name);

				setFormData({
					email: '',
					username: '',
					password: '',
					confirmPassword: '',
					firstName: '',
					lastName: '',
				});
				setErrors({});
				navigate('/login');
			} else {
				// Registration failed but no error was thrown
				toast.error('❌ Registration failed. Please try again.');
			}
		} catch (error) {
			logger.error('User registration failed', 'USER', { error });
			
			// Show error toaster
			toast.error('❌ Registration failed. Please try again.');
		}

		setIsSubmitting(false);
	};

	return (
		<div className={`min-h-screen flex items-center justify-center p-4 ${theme.background} relative overflow-hidden`}>
			{/* Background decoration */}
			<div className='fixed inset-0 overflow-hidden pointer-events-none'>
				<div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
				<div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
			</div>

			{/* Main container */}
			<div className='relative w-full max-w-md z-10'>
				{/* Glass morphism card - Enhanced */}
				<div className={`${theme.card} p-8 space-y-6`}>
					{/* Header */}
					<div className='text-center space-y-2'>
						<h1 className={`text-3xl font-bold ${theme.textHeading} leading-tight tracking-tight`}>
							Create Account
						</h1>
						<p className={`${theme.textSecondary} text-sm leading-relaxed`}>
							Join {APP_CONFIG.name} today!
						</p>
					</div>

					{/* Form Validation Errors */}
					{Object.keys(errors).length > 0 && <FormError errors={errors} />}
					
					{/* API Error */}
					{registerError && (
						<div className={`p-4 rounded-xl ${theme.alertError}`}>
							<div className="flex items-start space-x-3">
								<div className={`flex-shrink-0 ${theme.textError}`}>
									<svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
									</svg>
								</div>
								<div className="flex-1">
									<h4 className={`text-sm font-medium ${theme.textErrorHeading}`}>
										Registration Failed
									</h4>
									<p className={`mt-1 text-sm ${theme.textError}`}>
										{registerError}
									</p>
								</div>
							</div>
						</div>
					)}

					<form onSubmit={handleSubmit} className='space-y-4'>
						{/* Email Field */}
						<div className="form-control">
							<Input
								type='email'
								id='email'
								name='email'
								placeholder="Enter your email"
								value={formData.email}
								onChange={handleInputChange}
								error={errors.email}
								disabled={isSubmitting || registerLoading}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						{/* Username Field */}
						<div className="form-control">
							<Input
								type='text'
								id='username'
								name='username'
								placeholder="Choose a username"
								value={formData.username}
								onChange={handleInputChange}
								error={errors.username}
								disabled={isSubmitting || registerLoading}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						{/* Name Fields Row */}
						<div className="grid grid-cols-2 gap-4">
							{/* First Name Field */}
							<div className="form-control">
								<Input
									type='text'
									id='firstName'
									name='firstName'
									placeholder="First name"
									value={formData.firstName}
									onChange={handleInputChange}
									disabled={isSubmitting || registerLoading}
									className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
								/>
							</div>

							{/* Last Name Field */}
							<div className="form-control">
								<Input
									type='text'
									id='lastName'
									name='lastName'
									placeholder="Last name"
									value={formData.lastName}
									onChange={handleInputChange}
									disabled={isSubmitting || registerLoading}
									className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
								/>
							</div>
						</div>

						{/* Password Field */}
						<div className="form-control">
							<Input
								type='password'
								id='password'
								name='password'
								placeholder="Create a password"
								value={formData.password}
								onChange={handleInputChange}
								error={errors.password}
								disabled={isSubmitting || registerLoading}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						{/* Confirm Password Field */}
						<div className="form-control">
							<Input
								type='password'
								id='confirmPassword'
								name='confirmPassword'
								placeholder="Confirm your password"
								value={formData.confirmPassword}
								onChange={handleInputChange}
								error={errors.confirmPassword}
								disabled={isSubmitting || registerLoading}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

					{/* Submit Button */}
					<button
						type="submit"
						disabled={isSubmitting || registerLoading}
						className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${
							isSubmitting || registerLoading ? `${theme.buttonDisabled} ${theme.buttonTextDisabled}` : `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
						}`}
					>
						{isSubmitting || registerLoading ? (
							<span className='flex items-center justify-center gap-2'>
								<svg className='animate-spin h-5 w-5' fill='none' viewBox='0 0 24 24'>
									<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
									<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
								</svg>
								<span>Creating account...</span>
							</span>
						) : (
							<span>Create Account</span>
						)}
					</button>
					</form>

					{/* Divider */}
					<div className='relative py-3'>
						<div className='absolute inset-0 flex items-center'>
							<div className={`w-full border-t ${theme.separator}`}></div>
						</div>
						<div className='relative flex justify-center text-sm'>
							<span className={`px-4 py-1 ${theme.card} ${theme.textSecondary} rounded-full`}>
								Already have an account?
							</span>
						</div>
					</div>

					{/* Login Link */}
					<Link
						to={ROUTES.LOGIN}
						className={`block w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
					>
						Sign In Instead
					</Link>

					{/* Footer */}
					<div className={`text-center text-xs ${theme.textMuted} pt-1 leading-relaxed`}>
						By creating an account, you agree to our Terms of Service and Privacy Policy
					</div>
				</div>
			</div>
		</div>
	);
}

export default RegisterForm;